import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { post } from '@/services/api-client';

/**
 * POST /api/Billing/Services/Fees?pageNumber=1&pageSize=50&q=<query>
 *
 * Mirrors Flutter `fetchServicesFees()` →
 * `billing_module/data/datasources/billing_data_source_impl.dart:418-446`.
 * Body `type` switches between provider-specific ("provider") and the
 * full catalog ("all") — matches the Provider Fees / All tabs in the
 * Add Procedure Codes popup.
 */

export interface ServiceFee {
  id?: string;
  procedureCode?: { code?: string; displayName?: string };
  shortDescription?: string;
  fees?: { id?: string; fee?: number; location?: { id?: string; name?: string } }[];
}

export interface ServiceFeesResponse {
  pageNumber?: number;
  pageSize?: number;
  totalNumberOfPages?: number;
  totalNumberOfRecords?: number;
  results?: ServiceFee[];
}

interface UseServiceFeesParams {
  providerId?: string | null;
  locationId?: string | null;
  patientId?: string | null;
  type: 'provider' | 'all';
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}

export function useServiceFees(params: UseServiceFeesParams | null) {
  return useQuery({
    queryKey: ['service-fees', params],
    // The provider-fee tab needs a provider; the full catalog (`type: 'all'`)
    // does not — a DEVICE booking (PROD-2007) has no provider and prices its
    // invoice from catalog services alone.
    enabled: !!params && (params.type === 'all' || !!params.providerId),
    placeholderData: keepPreviousData,
    // Always hit the backend when this popup opens — never serve a cached
    // fee list.
    //
    // This hook sets no staleTime of its own, so it INHERITED the global
    // `staleTime: 5 * 60_000` from `app/providers/query-client.ts`. That made
    // reopening the Add-service popup inside five minutes render cached rows
    // with no request at all. Andrew, 2026-08-25: "don't cache in both" —
    // this surface and the billing/claims ServicePickerDialog.
    //
    // The global default is right for most screens (it exists to match
    // Flutter's fetch-once semantics); it is wrong for a live search popup,
    // where fees, provider assignment and patient eligibility may all have
    // moved since the last open — and the second open is usually someone
    // re-checking because a number looked wrong. `keepPreviousData` keeps the
    // refetch from blanking the list.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useServiceFees called without params');
      const qs = new URLSearchParams();
      qs.set('pageNumber', String(params.pageNumber ?? 1));
      qs.set('pageSize', String(params.pageSize ?? 50));
      if (params.query) qs.set('q', params.query);
      const body = {
        providerId: params.providerId ?? null,
        locationId: params.locationId ?? null,
        patientId: params.patientId ?? null,
        type: params.type,
      };
      return post<ServiceFeesResponse>(
        `/api/Billing/Services/Fees?${qs.toString()}`,
        body,
        { signal },
      );
    },
  });
}
